import {
  RoutineWorkflow,
  useRoutineAlerts,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  AlertSummary,
  Button,
  Icon,
  LoadingIndicator,
  SlideAction,
  tokens,
  useSlideLayoutSafe
} from '@gripp/shared-ui';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';

type AlertPanelProps = {
  assetId: string;
  routineId: string;
};

/**
 * AlertPanel renders the alert list inside the SlideLayout panel (showHeader: false).
 * Navigates to separate pages for creating or editing an alert.
 */
export const AlertPanel = ({ assetId, routineId }: AlertPanelProps) => {
  const navigate = useNavigate();
  const slideLayout = useSlideLayoutSafe();
  const location = useLocation();
  const { t } = useTranslation();
  const { workspaceAsset } = useWorkspaceAsset(assetId);
  const isAlertPage = location.pathname.includes('/alerts/');

  const { routineWorkflows, isLoading } = useRoutineAlerts(
    workspaceAsset?.id ?? '',
    routineId,
    undefined,
    { enabled: !!workspaceAsset?.id }
  );

  const handleAlertSelected = async (alert: RoutineWorkflow) => {
    if (!alert?.id) {
      return;
    }

    if (slideLayout && !isAlertPage) {
      await slideLayout.onSlideChange(SlideAction.NEXT);
    }
    
    navigate(`/assets/${assetId}/routines/${routineId}/alerts/${alert.id}/edit`);
  };

  const handleShowNewAlert = async() => {
    if (slideLayout && !isAlertPage) {
      await slideLayout.onSlideChange(SlideAction.NEXT);
    }

    navigate(`/assets/${assetId}/routines/${routineId}/alerts/new`);
  };

  return (
    <View style={styles.container}>
      {!workspaceAsset || isLoading ? (
        <View style={styles.loadingContainer}>
          <LoadingIndicator
            flexContainer
          />
        </View>
      ) : (
        <View style={styles.scrollViewContainer}>
          <ScrollView>
            {routineWorkflows?.map((item: RoutineWorkflow, index: number) => (
              <AlertSummary
                key={`${index}-${item.id}`}
                workflow={item}
                workspaceAsset={workspaceAsset}
                routineId={routineId}
                onAlertSelected={handleAlertSelected}
              />
            ))}
            <Button
              variant="rectangular"
              size="large"
              state="default"
              onPress={handleShowNewAlert}
              leftIcon={
                <Icon
                  name="Add"
                  size={20}
                  style={{ marginRight: tokens.spacing.md }}
                />
              }
              style={styles.createAlertButton}
            >
              {t('alerts.setAlert')}
            </Button>
          </ScrollView>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    margin: tokens.spacing.xl,
  },
  scrollViewContainer: {
    flex: 1,
    margin: tokens.spacing.xl,
  },
  createAlertButton: {
    marginTop: tokens.spacing.sm,
  },
});
